import React, { useEffect, useRef, useState } from "react";
import { getBranding } from "../../hooks/tennant";
import { useError } from "../../context/errorContext";
import SignatureCanvas from "react-signature-canvas";
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ReplayCircleFilledRoundedIcon from '@mui/icons-material/ReplayCircleFilledRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LoopOutlinedIcon from '@mui/icons-material/LoopOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import DisabledVisibleRoundedIcon from '@mui/icons-material/DisabledVisibleRounded';
import axiosInstance from "../../api/axiosInstance";
import useWakeLock from "../../hooks/screenWakeLock";
import moment from "moment/moment";
import "./sign.scss";

const Sign = () => {
    const [doc, setDoc] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [branding, setBranding] = useState(null);

    const sigCanvas = useRef(null);
    const wakeLockActive = useWakeLock();

    const { showMessage } = useError();

    useEffect(() => {
        const loadBranding = async () => {
            try {
                const data = await getBranding();
                setBranding(data);
            } catch (error) {
                console.error("Branding konnte nicht geladen werden:", error);
            }
        };

        loadBranding();
    }, []);

    useEffect(() => {
        const fetchDocument = async () => {
            try {
                setLastUpdate(new Date());

                const res = await axiosInstance.get("/sign/doc/1");

                setDoc(res.data)

            } catch (err) {
                console.error(err);
            }
        };

        fetchDocument();

        const interval = setInterval(fetchDocument, 5000);

        return () => clearInterval(interval);
    }, []);

    const clearSignature = () => {
        sigCanvas.current.clear();
    };

    const signDoc = async () => {
        if (!doc) {
            return;
        }

        if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
            showMessage("Bitte unterschreiben Sie zuerst!");
            return;
        }

        try {
            const signature = sigCanvas.current.toDataURL("image/png");

            await axiosInstance.post("/sign/doc", {
                serialNr: doc.WEIGHTSERIALNR,
                docNr: doc.WEIGHTID,
                signature
            });

            showMessage("Lieferschein erfolgreich unterschrieben!");

            sigCanvas.current.clear();
            setDoc(null);

        } catch (error) {
            if (error.response?.status === 409) {
                showMessage("Dieser Lieferschein wurde bereits unterschrieben!");
                setDoc(null);
            } else {
                console.error("Fehler beim Speichern der Unterschrift:", error);
                showMessage("Die Unterschrift konnte nicht gespeichert werden!");
            }
        }
    };

    return (
        <div className="sign">
            <div className="statusbar">
                {branding && <img src={`/assets/branding/${branding.logoNav}`} alt="Logo" />}
                <div className="refresh">
                    <span>Letzte Aktualisierung: {moment(lastUpdate).format("DD.MM.YYYY [um] HH:mm [Uhr]")}
                    </span>
                    <LoopOutlinedIcon />
                    {wakeLockActive && <DisabledVisibleRoundedIcon />}
                </div>
            </div>

            {doc ? (
                <div className="document">
                    <div className="grid">
                        <div className="row">
                            <div className="card">
                                <div className="header">
                                    <DescriptionOutlinedIcon />
                                    <div className="value">
                                        <h3>Lieferschein</h3>
                                    </div>
                                </div>
                                <div className="data">
                                    <div className="row">
                                        <table>
                                            <tbody>
                                                <tr>
                                                    <td>Lieferschein Nr.</td>
                                                    <td>{doc.WEIGHTSERIALNR}/{doc.WEIGHTID}</td>
                                                </tr>
                                                <tr>
                                                    <td>Vom</td>
                                                    <td>{moment(doc.DOCDATE).format("L")}</td>
                                                </tr>
                                                <tr>
                                                    <td>MPP Code</td>
                                                    <td>{doc.CUSTOMSTR3}</td>
                                                </tr>
                                                <tr>
                                                    <td>Transportbeginn</td>
                                                    <td>{doc.UPDATED ? (moment(doc.UPDATED.replace("Z", "")).format("L LT [Uhr]")) : "-"}</td>
                                                </tr>
                                                <tr>
                                                    <td>Transportmittel</td>
                                                    <td>{doc.LORRYREGIST}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="line" />
                                    <div className="row">
                                        <div className="group">
                                            <h5>Kundendaten</h5>
                                            <p>
                                                {doc.COMPANY1}
                                            </p>
                                        </div>
                                        <div className="group">
                                            <h5>Bestimmungsort</h5>
                                            <p>
                                                {doc.DESTNAME1} {doc.DESTNAME2}
                                            </p>
                                            <p>
                                                {doc.ZIP}, {doc.PLACE}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="grid">
                        <div className="row">
                            <div className="card">
                                <div className="articles">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th style={{ width: "100px" }}>Artikel Kodex</th>
                                                <th>Beschreibung</th>
                                                <th style={{ width: "40px" }}>VE</th>
                                                <th style={{ width: "80px" }}>Stück ME</th>
                                                <th style={{ width: "60px" }}>Menge</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>{doc.ARTICLEID}</td>
                                                <td>{doc.ARTICLEDESCRIPTION}</td>
                                                <td></td>
                                                <td>{doc.UNIT}</td>
                                                <td>{doc.QUANTITY.toLocaleString("de-DE")}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                        <div className="row right">
                            <div className="card">
                                <div className="signBox">
                                    <div className="header">
                                        <EditOutlinedIcon />
                                        <div className="value">
                                            <h4>Empfang bestätigen</h4>
                                            <span>Bitte unterschreiben Sie unten, um den Erhalt der Ware zu bestätigen</span>
                                        </div>
                                    </div>
                                    <div className="box">
                                        <SignatureCanvas
                                            ref={sigCanvas}
                                            penColor="black"
                                            canvasProps={{ className: "sigCanvas", width: 550, height: 200 }}
                                        />
                                        <div className="signLine" />
                                    </div>
                                </div>
                                <div className="signControls">
                                    <button className="light" onClick={() => setDoc(null)}>Abbrechen</button>
                                    <div className="right">
                                        <button className="light" onClick={clearSignature}>
                                            <ReplayCircleFilledRoundedIcon />
                                            Zurücksetzen
                                        </button>
                                        <button onClick={signDoc}>
                                            <CheckRoundedIcon />
                                            Bestätigen
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="noDoc">
                    <h3>Kein Dokument zum unterschreiben</h3>
                </div>
            )}
        </div>
    );
};

export default Sign;